import React from "react";
import { apiReferensi, apiUploadReferensi, apiHapusReferensi, referensiUrl, formatApiErrorDetail } from "@/lib/api";
import { Loader2, ImagePlus, Trash2 } from "lucide-react";
import * as W from "@/lib/waktu";

const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;
const MAKS_MB = 10;

// v2.18 C (owner 2026-10-24): example pictures from the customer for the designer. Kasir/CS, Desain and the
// Owner upload, the Manager only looks; the uploader or the Owner removes one. The server decides every right
// (boleh_unggah / boleh_hapus) and never shows these pictures on the SPK, lembar kerja, ACC page or nota.
export default function ReferensiDesain({ orderId, testid = "referensi", onChanged }) {
  const [d, setD] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const [msg, setMsg] = React.useState(null);
  const load = React.useCallback(() => apiReferensi(orderId).then(setD).catch((e) => setMsg(errText(e))), [orderId]);
  React.useEffect(() => { load(); }, [load]);
  if (!d) return msg ? <div className="text-xs text-red-300">{msg}</div> : null;
  const penuh = d.gambar.length >= d.maks;

  const unggah = async (file) => {
    if (!file) return;
    if (file.size > MAKS_MB * 1024 * 1024) { setMsg(`File terlalu besar (maks ${MAKS_MB} MB)`); return; }
    setBusy("unggah"); setMsg(null);
    try { await apiUploadReferensi(orderId, file); setMsg("Gambar referensi tersimpan"); await load(); onChanged?.(); }
    catch (e) { setMsg(errText(e)); } finally { setBusy(null); }
  };
  const hapus = async (g) => {
    if (!window.confirm("Hapus gambar referensi ini? Penghapusan tercatat.")) return;
    setBusy(g.id); setMsg(null);
    try { await apiHapusReferensi(orderId, g.id); setMsg("Gambar referensi dihapus"); await load(); onChanged?.(); }
    catch (e) { setMsg(errText(e)); } finally { setBusy(null); }
  };

  return (
    <div data-testid={testid} className="rounded-lg border border-slate-700 bg-slate-950/40 p-3 space-y-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-slate-200">Gambar referensi desain</span>
        <span data-testid={`${testid}-jumlah`} className="text-xs text-slate-400">{d.gambar.length} dari {d.maks}</span>
        <span className="text-[11px] text-slate-500">hanya untuk tim (tidak tampil di SPK, halaman ACC customer, maupun nota)</span>
        {d.boleh_unggah && (
          <label className={`ml-auto inline-flex items-center gap-1.5 rounded border px-3 py-1.5 ${penuh ? "cursor-not-allowed border-slate-800 text-slate-500" : "cursor-pointer border-slate-600 text-slate-200 hover:border-amber-500/50"}`}>
            {busy === "unggah" ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />} {penuh ? `Sudah ${d.maks} gambar` : "Tambah gambar"}
            <input data-testid={`${testid}-file`} type="file" accept="image/jpeg,image/png,image/webp" disabled={penuh} className="hidden"
              onChange={(e) => { unggah(e.target.files?.[0]); e.target.value = ""; }} /></label>
        )}
      </div>
      {d.gambar.length === 0 ? <p className="text-xs text-slate-500">Belum ada gambar referensi.</p> : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          {d.gambar.map((g, i) => (
            <div key={g.id} data-testid={`${testid}-gambar-${i}`} className="rounded-lg border border-slate-700 bg-white p-1.5">
              <a href={referensiUrl(orderId, g.id)} target="_blank" rel="noopener noreferrer">
                <img src={referensiUrl(orderId, g.id)} alt={`Referensi ${i + 1}`} className="mx-auto h-24 w-full object-contain" /></a>
              <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-500">
                <span className="truncate">{g.oleh_nama || g.oleh} · {W.waktu(g.waktu)}</span>
                {g.boleh_hapus && (
                  <button data-testid={`${testid}-hapus-${i}`} type="button" onClick={() => hapus(g)} disabled={busy === g.id} title="Hapus gambar"
                    className="ml-auto shrink-0 rounded p-1 text-slate-500 hover:text-red-500">
                    {busy === g.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {d.boleh_unggah && <p className="text-[11px] text-slate-500">JPG/PNG/WebP, maks {MAKS_MB} MB, sisi terpendek minimal 200 px, maksimal {d.maks} gambar per pesanan. Hanya pengunggah atau Owner yang bisa menghapus.</p>}
      {msg && <div data-testid={`${testid}-pesan`} className="text-xs text-slate-300">{msg}</div>}
    </div>
  );
}
